/**
 * Operation arrows, staff-map style: a smooth route with a body that swells from a thin tail and
 * ends in a broad filled head, drawn with a dark casing. Built into a LineBatch using per-vertex
 * widths; a single reference width keeps the taper proportional when the px clamp kicks in.
 */
import type { LineBatch } from '../gfx/lines';
import { polyLength } from './paths';

export interface ArrowStyle {
  color: number;
  alpha: number;
  /** Body width (world units). */
  width: number;
  casing?: number;
  casingAlpha?: number;
  dashed?: boolean;
  minPx?: number;
  maxPx?: number;
}

/** Evenly resample a polyline up to arc length `end` (step in world units). */
function resample(pts: number[], end: number, step: number): number[] {
  const out: number[] = [pts[0], pts[1]];
  let acc = 0;
  let next = step;
  for (let i = 2; i < pts.length; i += 2) {
    const ax = pts[i - 2];
    const ay = pts[i - 1];
    const dx = pts[i] - ax;
    const dy = pts[i + 1] - ay;
    const l = Math.hypot(dx, dy);
    while (next <= Math.min(acc + l, end) && l > 0) {
      const t = (next - acc) / l;
      out.push(ax + dx * t, ay + dy * t);
      next += step;
    }
    acc += l;
    if (acc >= end) break;
  }
  const [ex, ey] = at(pts, end);
  const lx = out[out.length - 2];
  const ly = out[out.length - 1];
  if (Math.hypot(ex - lx, ey - ly) > step * 0.25) out.push(ex, ey);
  else {
    out[out.length - 2] = ex;
    out[out.length - 1] = ey;
  }
  return out;
}

function at(pts: number[], s: number): [number, number] {
  let acc = 0;
  for (let i = 2; i < pts.length; i += 2) {
    const dx = pts[i] - pts[i - 2];
    const dy = pts[i + 1] - pts[i - 1];
    const l = Math.hypot(dx, dy);
    if (acc + l >= s) {
      const t = l > 0 ? (s - acc) / l : 0;
      return [pts[i - 2] + dx * t, pts[i - 1] + dy * t];
    }
    acc += l;
  }
  return [pts[pts.length - 2], pts[pts.length - 1]];
}

export function addArrow(b: LineBatch, pts: number[], st: ArrowStyle): void {
  const len = polyLength(pts);
  if (len < 6) return;
  const w = st.width;
  const headLen = Math.min(len * 0.42, w * 3.1);
  const headW = w * 2.75;
  const bodyEnd = len - headLen;
  const body = resample(pts, bodyEnd, Math.max(3, w * 0.8));
  const widths: number[] = [];
  let acc = 0;
  const ramp = Math.min(len * 0.45, 70);
  for (let i = 0; i < body.length; i += 2) {
    if (i > 0) acc += Math.hypot(body[i] - body[i - 2], body[i + 1] - body[i - 1]);
    const t = Math.min(1, acc / ramp);
    widths.push(w * (0.35 + 0.65 * t * t * (3 - 2 * t)));
  }
  const [hx, hy] = [body[body.length - 2], body[body.length - 1]];
  const tx = pts[pts.length - 2];
  const ty = pts[pts.length - 1];
  const dl = Math.hypot(tx - hx, ty - hy) || 1;
  const ux = (tx - hx) / dl;
  const uy = (ty - hy) / dl;
  const minPx = st.minPx ?? 3;
  const maxPx = st.maxPx ?? 14;
  const opts = (ws: number[]) => ({ widths: ws, refWidth: w });
  if (st.casing !== undefined) {
    const ca = st.casingAlpha ?? 0.45;
    const cw = w * 0.32 + 0.9;
    b.add(
      body,
      { width: w, color: st.casing, alpha: ca, minPx, maxPx, dash: st.dashed ? w * 1.6 : 0, gap: w * 1.1 },
      opts(widths.map((x) => x + cw)),
    );
    b.add(
      [hx - ux * 0.5, hy - uy * 0.5, tx + ux * 1.6, ty + uy * 1.6],
      { width: w, color: st.casing, alpha: ca, minPx, maxPx },
      opts([headW + cw * 1.6, 0.01]),
    );
  }
  b.add(
    body,
    { width: w, color: st.color, alpha: st.alpha, minPx, maxPx, dash: st.dashed ? w * 1.6 : 0, gap: w * 1.1 },
    opts(widths),
  );
  b.add([hx, hy, tx, ty], { width: w, color: st.color, alpha: st.alpha, minPx, maxPx }, opts([headW, 0.01]));
}

/** A gently bowed route between two points (cubic Bézier, bow ∝ distance). */
export function bowedRoute(x0: number, y0: number, x1: number, y1: number, bow = 0.16): number[] {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const nx = -dy * bow;
  const ny = dx * bow;
  const out: number[] = [];
  const steps = Math.max(6, Math.min(28, Math.round(Math.hypot(dx, dy) / 12)));
  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    const b = 4 * t * (1 - t);
    out.push(x0 + dx * t + nx * b, y0 + dy * t + ny * b);
  }
  return out;
}
